/**
 * Widget: Team Leaderboard
 * Shows top team members by activity
 */

import React from 'react';
import { Trophy, Medal, Award } from 'lucide-react';
import { mockTeamMembers } from '../../data/mockTeamData';
import { journeyTracker } from '../../lib/journeyTracker';

interface TeamLeaderboardProps {
  metric?: 'exploration' | 'achievements' | 'activity';
  maxDisplay?: number;
  showYourRank?: boolean;
}

export const TeamLeaderboard: React.FC<TeamLeaderboardProps> = ({
  metric = 'exploration',
  maxDisplay = 10,
  showYourRank = true,
}) => {
  const journey = journeyTracker.getJourney();

  // Calculate scores for team members
  const teamScores = mockTeamMembers.map((member) => {
    let score = 0;
    
    if (metric === 'exploration') {
      // Use actual frameworks explored
      score = member.frameworksExplored + Math.floor(Math.random() * 3);
    } else if (metric === 'achievements') {
      // Simulate achievements based on outcomes contributed
      score = member.outcomesContributed + Math.floor(Math.random() * 3);
    } else {
      // Activity score (composite)
      score = Math.min(100, 
        member.frameworksExplored * 2 + 
        member.outcomesContributed * 5 +
        Math.floor(Math.random() * 10)
      );
    }

    return {
      id: member.id,
      name: member.name,
      role: member.role === 'leader' ? 'Team Leader' : member.role === 'member' ? 'Team Member' : 'Contributor',
      score,
      activity: 'active' as const,
    };
  });

  // Add current user
  const userScore = metric === 'exploration'
    ? ((journey as any).visitedPages?.length || 0)
    : metric === 'achievements'
    ? ((journey as any).achievements?.length || 0)
    : Math.min(100, ((journey as any).visitedPages?.length || 0) * 3 + ((journey as any).achievements?.length || 0) * 5);

  const allScores = [
    ...teamScores,
    {
      id: 'current-user',
      name: 'You',
      role: 'Your Progress',
      score: userScore,
      activity: 'current' as const,
    }
  ].sort((a, b) => b.score - a.score);

  const topScores = allScores.slice(0, maxDisplay);
  const userRank = allScores.findIndex(s => s.id === 'current-user') + 1;
  const userInTop = userRank <= maxDisplay;

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="h-5 w-5 text-amber-500" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-gray-400" />;
    if (rank === 3) return <Award className="h-5 w-5 text-amber-700" />;
    return <span className="text-xs font-mono text-charcoal-500 w-5 text-center">#{rank}</span>;
  };

  const getMetricLabel = () => {
    if (metric === 'exploration') return 'Frameworks Explored';
    if (metric === 'achievements') return 'Achievements';
    return 'Activity Score';
  };

  return (
    <div className="bg-white rounded-xl border-2 border-charcoal-200 overflow-hidden">
      <div className="bg-gradient-to-r from-primary-600 to-sage-600 p-4">
        <h3 className="text-lg font-bold text-white flex items-center gap-2">
          <Trophy className="h-5 w-5" />
          Team Leaderboard
        </h3>
        <p className="text-sm text-white/90 mt-1">{getMetricLabel()}</p>
      </div>

      <div className="divide-y divide-charcoal-100">
        {topScores.map((entry, index) => {
          const rank = index + 1;
          const isCurrentUser = entry.id === 'current-user';

          return (
            <div
              key={entry.id}
              className={`p-3 flex items-center gap-3 ${
                isCurrentUser
                  ? 'bg-primary-50 border-l-4 border-primary-600'
                  : rank <= 3
                  ? 'bg-amber-50/30'
                  : 'hover:bg-charcoal-50'
              } transition-colors`}
            >
              <div className="w-8 flex items-center justify-center">
                {getRankIcon(rank)}
              </div>

              <div className="flex-1 min-w-0">
                <div className={`text-sm font-semibold ${
                  isCurrentUser ? 'text-primary-700' : 'text-charcoal-900'
                } truncate`}>
                  {entry.name}
                  {isCurrentUser && (
                    <span className="ml-2 text-xs bg-primary-600 text-white px-2 py-0.5 rounded-full">
                      You
                    </span>
                  )}
                </div>
                <div className="text-xs text-charcoal-600 truncate">
                  {entry.role}
                </div>
              </div>

              <div className="text-right">
                <div className={`text-lg font-bold ${
                  isCurrentUser ? 'text-primary-700' : 'text-charcoal-900'
                }`}>
                  {entry.score}
                </div>
                {rank <= 3 && !isCurrentUser && (
                  <div className="text-xs text-amber-600 font-semibold">
                    Top {rank}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showYourRank && !userInTop && (
        <div className="border-t-2 border-primary-200 bg-primary-50 p-3">
          <div className="flex items-center gap-3">
            <div className="w-8 flex items-center justify-center">
              <span className="text-xs font-mono text-primary-700 font-bold">
                #{userRank}
              </span>
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-primary-900">Your Rank</div>
              <div className="text-xs text-primary-700">
                {userScore} {metric === 'exploration' ? 'frameworks' : metric === 'achievements' ? 'achievements' : 'points'}
              </div>
            </div>
            <div className="text-sm text-primary-700">
              {topScores[topScores.length - 1]?.score - userScore > 0 && (
                <span className="text-xs">
                  {topScores[topScores.length - 1].score - userScore} to Top {maxDisplay}
                </span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

